import React, { useEffect, useState } from 'react';
import AskIcon from 'shared/components/ui/AskIcon';
import { Pause, Pencil, Play, Trash2 } from 'lucide-react';
import { useAppDialog } from 'shared/context/AppDialogContext';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import { getSelfImproving, deploySelfImproving, updateSelfImproving, setSelfImprovingLive, runSelfImprovingNow, deleteSelfImproving } from 'features/evals3/api/evals3Api';
import { Field, inlineRow, numInput, formatDateTime } from './formParts';

/**
 * Self-improving on one eval.
 *
 * Deploying it makes two ordinary things, each editable where it lives: an
 * ALERT on the eval (when to look) and an AGENT that starts from it and has
 * Ask improve the eval — a proposed change in Proposed changes, and what to
 * fix in the agent. The panel reads whether that pair is live; it stores
 * nothing of its own.
 */

const MEASURES = [
  { value: 'failures', label: 'Failures', unit: 'count' },
  { value: 'pass_rate', label: 'Pass rate', unit: 'percent' },
  { value: 'thumbs_down', label: 'Results marked wrong', unit: 'count' },
  { value: 'audit_disagreements', label: 'Audit disagreements', unit: 'count' },
  { value: 'errored', label: 'Errored', unit: 'count' },
  { value: 'waiting', label: 'Waiting for a person', unit: 'count' },
  { value: 'score', label: 'Average score', unit: 'score' },
];
const OPERATORS = [
  { value: 'at_or_above', label: 'at or above' }, { value: 'above', label: 'above' },
  { value: 'below', label: 'below' }, { value: 'at_or_below', label: 'at or below' },
];
const WINDOWS = [
  { value: '900', label: '15 minutes' }, { value: '3600', label: '1 hour' }, { value: '21600', label: '6 hours' },
  { value: '86400', label: '24 hours' }, { value: '604800', label: '7 days' },
];
const measureWord = (m) => MEASURES.find((x) => x.value === m)?.label || m;
const opWord = (o) => OPERATORS.find((x) => x.value === o)?.label || o;
const windowWord = (s) => WINDOWS.find((x) => Number(x.value) === Number(s))?.label || `${Math.round(Number(s) / 60)} minutes`;
const valueWord = (m, v) => (m === 'pass_rate' ? `${v}%` : String(v));

const btn = 'inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[12px] font-medium transition-colors disabled:opacity-60';
const secondary = `${btn} border border-[color:var(--border-default)] bg-[color:var(--surface-base)] text-[color:var(--text-primary)] hover:bg-surface-hover`;
/** Lucid's mark is always the brand orange, so a Lucid action is never a filled orange button it would vanish into. */
const lucidMark = <AskIcon size={13} className="text-[color:var(--brand-primary)]" />;

export default function SelfImprovingPanel({ teamId, lineageId, readOnly = false }) {
  const { confirm } = useAppDialog();
  const [st, setSt] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null); // open deploy form
  const [busy, setBusy] = useState(false);
  const [ran, setRan] = useState(null);

  useEffect(() => {
    let off = false;
    if (!teamId || !lineageId) return undefined;
    getSelfImproving(teamId, lineageId).then((d) => { if (!off) setSt(d); }).catch((e) => { if (!off) setError(e?.response?.data?.message || e.message); });
    return () => { off = true; };
  }, [teamId, lineageId]);

  const openForm = () => {
    const c = st?.defaults?.condition || { measure: 'failures', operator: 'at_or_above', threshold: 5, window_seconds: 3600 };
    setForm({ measure: c.measure, operator: c.operator, threshold: String(c.threshold), window_seconds: String(c.window_seconds), instructions: st?.defaults?.instructions || '', paused: false });
  };
  const openEdit = () => {
    const p = st.pairs[0];
    setForm({
      editing: true, measure: p.alert.measure, operator: p.alert.operator, threshold: String(p.alert.threshold),
      window_seconds: String(p.alert.window_seconds), instructions: p.agent?.instructions || st.defaults?.instructions || '', paused: false,
    });
  };
  const deploy = async () => {
    setBusy(true); setError(null);
    try {
      const body = {
        measure: form.measure, operator: form.operator, threshold: Number(form.threshold), window_seconds: Number(form.window_seconds),
        instructions: form.instructions,
      };
      const out = form.editing
        ? await updateSelfImproving(teamId, lineageId, { ...body, alert_id: st.pairs[0].alert.id })
        : await deploySelfImproving(teamId, lineageId, { ...body, paused: form.paused });
      setSt(out); setForm(null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };
  const runNow = async () => {
    setBusy(true); setError(null); setRan(null);
    try {
      const out = await runSelfImprovingNow(teamId, lineageId);
      setRan('Running now. Issues it files and changes it proposes appear in a few minutes.');
      return out;
    } catch (e) { setError(e?.response?.data?.message || e.message); return null; } finally { setBusy(false); }
  };
  // The alert and the agent belong to this eval: they are deleted here, together, and nowhere else.
  const remove = async () => {
    const ok = await confirm('Its alert and its agent are deleted. Issues and proposed changes it already made stay.', { title: 'Delete self-improving?', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    setBusy(true); setError(null);
    try { setSt(await deleteSelfImproving(teamId, lineageId)); } catch (e) { setError(e?.response?.data?.message || e.message); } finally { setBusy(false); }
  };
  const setLive = async (live) => {
    setBusy(true); setError(null);
    try { setSt(await setSelfImprovingLive(teamId, lineageId, live)); } catch (e) { setError(e?.response?.data?.message || e.message); } finally { setBusy(false); }
  };

  if (!st && !error) return <div className="text-[12px] text-[color:var(--text-muted)]">Checking…</div>;
  const pairs = st?.pairs || [];
  const editing = Boolean(form?.editing);

  const formEl = form ? (
        <div className="grid gap-2 rounded-md border border-[color:var(--border-default)] p-3" data-testid="self-improving-form">
          <div className={inlineRow}>
            <span>When</span>
            <div className="w-44"><BrandDropdown compact ariaLabel="Measure" value={form.measure} onChange={(v) => setForm((f) => ({ ...f, measure: v }))} options={MEASURES.map((m) => ({ value: m.value, label: m.label }))} /></div>
            <span>is</span>
            <div className="w-28"><BrandDropdown compact ariaLabel="Operator" value={form.operator} onChange={(v) => setForm((f) => ({ ...f, operator: v }))} options={OPERATORS} /></div>
            <input type="number" value={form.threshold} aria-label="Threshold" onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))} className={numInput} />
            {form.measure === 'pass_rate' && <span>%</span>}
            <span>over</span>
            <div className="w-32"><BrandDropdown compact ariaLabel="Window" value={form.window_seconds} onChange={(v) => setForm((f) => ({ ...f, window_seconds: v }))} options={WINDOWS} /></div>
          </div>
          <Field label="What Lucid should do">
            <textarea value={form.instructions} rows={4} aria-label="Instructions" onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))} className="input w-full text-[12px]" />
          </Field>
          {editing ? null : (
            <label className="flex items-center gap-2 text-[12px] text-[color:var(--text-secondary)]">
              <input type="checkbox" checked={form.paused} onChange={(e) => setForm((f) => ({ ...f, paused: e.target.checked }))} aria-label="Start paused" /> Start paused
            </label>
          )}
          <div className="flex items-center gap-2">
            <button type="button" onClick={deploy} disabled={busy || !form.threshold}
              title={editing ? undefined : 'Creates an alert on this eval and an agent that improves it'}
              className={editing ? `${btn} bg-brand text-ink-onBrand hover:bg-brand-hover` : secondary}>
              {editing ? 'Save' : <>{lucidMark} Deploy</>}
            </button>
            <button type="button" onClick={() => setForm(null)} className={`${btn} text-[color:var(--text-secondary)] hover:bg-surface-hover`}>Cancel</button>
          </div>
        </div>
  ) : null;

  return (
    <div data-testid="self-improving" className="grid gap-2">
      {pairs.length > 0 && editing ? formEl : pairs.length > 0 ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[11px] font-semibold ${st.live ? 'bg-success-soft text-success' : 'bg-[color:var(--surface-sunken)] text-[color:var(--text-secondary)]'}`} data-testid="self-improving-state">
              <span className={`h-1.5 w-1.5 rounded-full ${st.live ? 'bg-[color:var(--feedback-success)]' : 'border border-[color:var(--text-disabled)]'}`} aria-hidden />
              {st.live ? 'Live' : 'Paused'}
            </span>
            {!readOnly && (
              <button type="button" onClick={() => setLive(!st.live)} disabled={busy}
                className={secondary}>
                {st.live ? <><Pause size={12} aria-hidden /> Pause</> : <><Play size={12} aria-hidden /> Resume</>}
              </button>
            )}
            {!readOnly && (
              <button type="button" onClick={openEdit} disabled={busy}
                className={secondary}>
                <Pencil size={12} aria-hidden /> Edit
              </button>
            )}
            {!readOnly && (
              <button type="button" onClick={runNow} disabled={busy} title="Run the agent once now, with the alert's current value"
                className={secondary}>
                {lucidMark} Run now
              </button>
            )}
            {!readOnly && (
              <button type="button" onClick={remove} disabled={busy} aria-label="Delete self-improving" title="Delete its alert and its agent"
                className={`${btn} text-[color:var(--text-muted)] hover:bg-danger-soft hover:text-[color:var(--feedback-error)]`}>
                <Trash2 size={12} aria-hidden />
              </button>
            )}
          </div>
          {pairs.map((p) => (
            <div key={p.alert.id} className="rounded-md border border-[color:var(--border-subtle)] px-3 py-2 text-[12px] text-[color:var(--text-secondary)]">
              <div>
                When <span className="font-medium text-[color:var(--text-primary)]">{measureWord(p.alert.measure).toLowerCase()}</span> is {opWord(p.alert.operator)}{' '}
                <span className="font-medium text-[color:var(--text-primary)]">{valueWord(p.alert.measure, p.alert.threshold)}</span> over {windowWord(p.alert.window_seconds)},
                Lucid analyses the results behind it, files issues and proposes eval changes.
              </div>
              {p.alert.last_triggered_at && (
                <div className="mt-1 text-[11.5px] tabular-nums text-[color:var(--text-muted)]">last fired {formatDateTime(p.alert.last_triggered_at)}</div>
              )}
              {/* An alert that fires and an agent that fails looks exactly
                  like an eval improving itself. It is worse than none at
                  all, so it says so here rather than only in the agent's
                  own run history. */}
              {p.agent?.last_run?.status === 'failed' && (
                <div role="alert" className="mt-1.5 rounded border border-[color:var(--feedback-error)] bg-danger-soft px-2 py-1.5 text-[11.5px] text-danger" data-testid="self-improving-last-run">
                  It last ran {formatDateTime(p.agent.last_run.started_at)} and failed, so nothing was proposed.
                  {p.agent.last_run.error ? <span className="mt-0.5 block font-mono text-[11px] opacity-90">{p.agent.last_run.error}</span> : null}
                </div>
              )}
            </div>
          ))}
        </>
      ) : !form ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-[color:var(--text-muted)]">Off</span>
          {!readOnly && (
            <button type="button" onClick={openForm} title="Lucid looks into the eval when it starts deciding badly" className={secondary}>
              {lucidMark} Deploy self-improving
            </button>
          )}
        </div>
      ) : (
        formEl
      )}
      {ran && <div className="text-[11.5px] text-[color:var(--text-secondary)]" role="status">{ran}</div>}
      {error && <div className="text-[11.5px] text-danger" role="alert">{error}</div>}
    </div>
  );
}
